import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Send, ChevronLeft, MoreHorizontal } from "lucide-react";
import { logger } from "../../utils/logger";
import { scormService } from "../../utils/scormService";
import { ChatMessageContent } from "./ChatMessageContent";
import { OtpNotification } from "./OtpNotification";
import { FakeLandingPage } from "./FakeLandingPage";
import type { LandingPage, LandingPageSubmission } from "./FakeLandingPage";
import { SmishingInputHelper } from "./SmishingInputHelper";
import { buildSmishingInitRequest, buildSmishingMessageRequest, type SmishingChatInitResponse, type SmishingChatReplyResponse, type SmishingRequestCategory } from "./smishing-chat-api";

type TeamsStatus = "idle" | "sending" | "receiving" | "ending" | "error" | "finished";

interface TeamsMessage {
  role: "user" | "assistant";
  content: string;
  timestamp?: string;
}

interface TeamsChatProps {
  microlearningId: string;
  language?: string;
  endpoint?: string;
  senderName?: string;
  senderNumber?: string;
  startLabel?: string;
  statusTexts?: Partial<Record<TeamsStatus, string>>;
  linkWarningTitle?: string;
  linkWarningBody?: string;
  otpTexts?: { codeLabel?: string; codeSuffix?: string; backLabel?: string; backHint?: string };
  landingPageTexts?: { backLabel?: string };
  uiTexts?: {
    now?: string;
    inputPlaceholder?: string;
    online?: string;
    typing?: string;
    unknownSender?: string;
    startChat?: string;
    chatEnded?: string;
    errorGeneric?: string;
    errorStartChat?: string;
    errorGetReply?: string;
  };
  embedded?: boolean;
  className?: string;
  onStatusChange?: (status: TeamsStatus) => void;
  onCompletionChange?: (completed: boolean) => void;
}

const FALLBACK_SMISHING_ENDPOINT = "https://agentic-ai-agent.keepnetlabs.com/smishing/chat";
const DEFAULT_SMISHING_ENDPOINT = typeof window !== "undefined" && (
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1"
) ? "http://localhost:4111/smishing/chat" : FALLBACK_SMISHING_ENDPOINT;

const getAccessToken = (): string | null => {
  if (typeof window === "undefined") return null;
  return new URLSearchParams(window.location.search).get("accessToken");
};

const formatTime = () => {
  const now = new Date();
  return now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

// Teams typing indicator
function TypingIndicator({ name }: { name: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-1">
      <div className="flex gap-0.5">
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#5B5FC7] [animation-delay:0ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#5B5FC7] [animation-delay:150ms]" />
        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-[#5B5FC7] [animation-delay:300ms]" />
      </div>
      <span className="text-[12px] text-[#616161]">{name} is typing...</span>
    </div>
  );
}

// Teams logo
function TeamsLogo({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="white">
      <path d="M19.404 4.478c0 1.26-.79 2.335-1.903 2.76v.001a2.87 2.87 0 0 1-.967.166 2.884 2.884 0 0 1-2.87-2.87L13.665.05h-.001A2.884 2.884 0 0 1 16.534.05a2.884 2.884 0 0 1 2.87 2.87v1.558zM22.543 7.498c0-.78-.632-1.413-1.413-1.413h-4.136a3.86 3.86 0 0 0 .525-1.942V2.87c0-.516-.101-1.009-.285-1.46h3.896c.78 0 1.413.633 1.413 1.414v4.674zm-2.826 1.178v5.891c0 2.728-2.212 4.94-4.94 4.94a4.91 4.91 0 0 1-2.584-.732v.001a6.948 6.948 0 0 1-4.347 4.058 6.89 6.89 0 0 1-2.24.372c-3.826 0-6.926-3.1-6.926-6.926 0-1.994.844-3.791 2.193-5.054v-.001A5.476 5.476 0 0 1 4.46 7.498h15.257zm-8.483 5.891V9.126H4.46c-.84 0-1.624.24-2.288.654v.001a5.476 5.476 0 0 0-.852 8.498 5.47 5.47 0 0 0 4.287 1.53 5.494 5.494 0 0 0 3.76-2.036 5.47 5.47 0 0 0 1.867-4.206z" />
    </svg>
  );
}

export function TeamsChat({
  microlearningId,
  language = "en-gb",
  endpoint = DEFAULT_SMISHING_ENDPOINT,
  senderName,
  statusTexts,
  linkWarningTitle,
  linkWarningBody,
  otpTexts,
  landingPageTexts,
  uiTexts,
  embedded = false,
  className = "",
  onStatusChange,
  onCompletionChange
}: TeamsChatProps) {
  const [status, setStatus] = useState<TeamsStatus>("idle");
  const [messages, setMessages] = useState<TeamsMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [hasStarted, setHasStarted] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [otpData, setOtpData] = useState<{ code: string; sender: string } | null>(null);
  const [expectedResponseCategory, setExpectedResponseCategory] = useState<SmishingRequestCategory | undefined>(undefined);
  const shownOtpCodes = useRef<Set<string>>(new Set());
  const activeOtpRef = useRef<{ code: string; sender: string } | null>(null);
  const [activeLandingPage, setActiveLandingPage] = useState<LandingPage | null>(null);
  const landingPageDataRef = useRef<LandingPage | null>(null);
  const pendingLandingPageRef = useRef<LandingPageSubmission | null>(null);
  const clickedLinkRef = useRef<string | null>(null);
  const autoSendRef = useRef(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const initRequestedRef = useRef(false);

  // Get user info from SCORM LMS
  const studentInfo = useMemo(() => scormService.getStudentInfo(), []);
  const resolvedSenderName = senderName || uiTexts?.unknownSender || "Unknown";
  const userName = studentInfo.name || "You";
  const userInitial = userName.charAt(0).toUpperCase();
  const senderInitial = resolvedSenderName.charAt(0).toUpperCase();

  const updateStatus = useCallback((next: TeamsStatus) => {
    setStatus(next);
    onStatusChange?.(next);
  }, [onStatusChange]);

  const statusLabel = useMemo(() => {
    const defaultLabels: Record<TeamsStatus, string> = {
      idle: uiTexts?.startChat || "Tap to start",
      sending: "Sending\u2026",
      receiving: "Typing\u2026",
      ending: "Ending\u2026",
      finished: uiTexts?.chatEnded || "Chat ended",
      error: uiTexts?.errorGeneric || "Something went wrong"
    };
    return statusTexts?.[status] ?? statusTexts?.idle ?? defaultLabels[status];
  }, [status, statusTexts, uiTexts]);

  const scrollToBottom = useCallback(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const initChat = useCallback(async () => {
    if (hasStarted || isInitializing) return;
    setIsInitializing(true);
    updateStatus("sending");
    setErrorMessage(null);
    try {
      const token = getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-AGENTIC-ALLY-TOKEN"] = token;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(buildSmishingInitRequest(microlearningId, language, "teams"))
      });
      const data = await response.json() as SmishingChatInitResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Init failed: ${response.status}`);
      }
      if (data.firstMessage) {
        setMessages([{ role: "assistant" as const, content: data.firstMessage, timestamp: formatTime() }]);
      }
      // Build-time OTP: show notification shortly after chat opens
      if (data.otpCode && data.otpSender && !shownOtpCodes.current.has(data.otpCode)) {
        shownOtpCodes.current.add(data.otpCode);
        setOtpData({ code: data.otpCode, sender: data.otpSender });
        activeOtpRef.current = { code: data.otpCode, sender: data.otpSender };
      }
      if (data.landingPage) {
        landingPageDataRef.current = data.landingPage;
      }
      if (data.isFinished) {
        setExpectedResponseCategory(undefined);
        setIsFinished(true);
        updateStatus("finished");
        onCompletionChange?.(true);
      } else {
        setExpectedResponseCategory(data.expectedResponseCategory);
        setHasStarted(true);
        updateStatus("idle");
      }
    } catch (error) {
      updateStatus("error");
      setErrorMessage(uiTexts?.errorStartChat || "Failed to start chat.");
      logger.push({
        level: "error",
        code: "TEAMS_INIT_FAIL",
        message: "Failed to init teams chat",
        detail: String(error)
      });
    } finally {
      setIsInitializing(false);
    }
  }, [endpoint, hasStarted, isInitializing, language, microlearningId, onCompletionChange, uiTexts?.errorStartChat, updateStatus]);

  useEffect(() => {
    if (initRequestedRef.current) return;
    initRequestedRef.current = true;
    initChat();
  }, [initChat]);

  const sendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || status !== "idle" || !hasStarted || isFinished) return;
    const nextMessages: TeamsMessage[] = [...messages, { role: "user" as const, content: trimmed, timestamp: formatTime() }];
    setMessages(nextMessages);
    setInputValue("");
    setExpectedResponseCategory(undefined);
    updateStatus("receiving");
    setErrorMessage(null);

    try {
      const token = getAccessToken();
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["X-AGENTIC-ALLY-TOKEN"] = token;

      const response = await fetch(endpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(buildSmishingMessageRequest({
          microlearningId,
          language,
          channel: "teams",
          messages: nextMessages,
          pendingOtp: activeOtpRef.current,
          pendingLandingPageSubmission: pendingLandingPageRef.current,
          pendingClickedLink: clickedLinkRef.current,
        }))
      });
      pendingLandingPageRef.current = null;
      clickedLinkRef.current = null;
      const data = await response.json() as SmishingChatReplyResponse;
      if (!response.ok || !data.success) {
        throw new Error(data.error || `Chat failed: ${response.status}`);
      }
      const replyParts = data.replies && data.replies.length > 1 ? data.replies : data.reply ? [data.reply] : [];
      if (replyParts.length > 0) {
        // First message immediately
        let accumulated: TeamsMessage[] = [...nextMessages, { role: "assistant" as const, content: replyParts[0], timestamp: formatTime() }];
        setMessages(accumulated);

        // Subsequent messages with staggered delay (typing effect)
        for (let i = 1; i < replyParts.length; i++) {
          updateStatus("receiving");
          await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1500));
          accumulated = [...accumulated, { role: "assistant" as const, content: replyParts[i], timestamp: formatTime() }];
          setMessages(accumulated);
        }
      }
      // Trigger OTP notification if backend sent one
      if (data.otpCode && data.otpSender && !shownOtpCodes.current.has(data.otpCode)) {
        shownOtpCodes.current.add(data.otpCode);
        setOtpData({ code: data.otpCode, sender: data.otpSender });
        activeOtpRef.current = { code: data.otpCode, sender: data.otpSender };
      }
      // Store landing page data — shown only when user taps the link
      landingPageDataRef.current = data.landingPage || null;
      if (data.isFinished) {
        setExpectedResponseCategory(undefined);
        setIsFinished(true);
        updateStatus("finished");
        onCompletionChange?.(true);
      } else {
        setExpectedResponseCategory(data.expectedResponseCategory);
        updateStatus("idle");
      }
    } catch (error) {
      updateStatus("error");
      setErrorMessage(uiTexts?.errorGetReply || "Failed to get reply.");
      logger.push({
        level: "error",
        code: "TEAMS_CHAT_FAIL",
        message: "Failed to fetch teams reply",
        detail: String(error)
      });
    }
  }, [endpoint, hasStarted, inputValue, isFinished, language, messages, microlearningId, onCompletionChange, status, uiTexts?.errorGetReply, updateStatus]);

  const handleLinkClick = useCallback((url: string): boolean => {
    clickedLinkRef.current = url;
    if (landingPageDataRef.current && landingPageDataRef.current.url === url) {
      setActiveLandingPage(landingPageDataRef.current);
      return true;
    }
    return false;
  }, []);

  const handleLandingPageSubmit = useCallback((submission: LandingPageSubmission) => {
    pendingLandingPageRef.current = submission;
    clickedLinkRef.current = null;
    setActiveLandingPage(null);
    setInputValue(Object.values(submission.fields).join(", "));
    autoSendRef.current = true;
  }, []);

  const handleLandingPageBack = useCallback(() => {
    setActiveLandingPage(null);
  }, []);

  // Auto-send after landing page form submission
  useEffect(() => {
    if (autoSendRef.current && inputValue.trim()) {
      autoSendRef.current = false;
      sendMessage();
    }
  }, [inputValue, sendMessage]);

  const isBusy = status === "sending" || status === "receiving" || isInitializing || isFinished;
  const previewText = messages[0]?.content || statusLabel;
  const wrapperClassName = embedded
    ? `w-full ${className}`
    : `w-full max-w-[420px] glass-border-3 p-4 ${className}`;
  const chatShellClassName = embedded
    ? "flex h-full min-h-0 flex-col overflow-hidden rounded-lg bg-white"
    : "flex h-full min-h-[420px] flex-col overflow-hidden rounded-lg bg-white";

  return (
    <div className={wrapperClassName}>
      {!isOpen ? (
        <div className="relative flex min-h-[380px] flex-col items-center justify-center">
          {/* Teams-style notification card */}
          <button
            type="button"
            onClick={() => {
              setIsOpen(true);
              if (!hasStarted) {
                initChat();
              }
            }}
            className="group relative w-full max-w-[270px] overflow-hidden rounded-[18px] border border-white/30 bg-white/80 p-3 text-left shadow-[0_4px_24px_rgba(0,0,0,0.08)] backdrop-blur-xl transition-all duration-200 hover:scale-[1.01] hover:shadow-[0_8px_32px_rgba(0,0,0,0.12)] active:scale-[0.99] dark:border-white/10 dark:bg-[#2C2C2E]/90 dark:shadow-[0_4px_24px_rgba(0,0,0,0.3)]"
          >
            {/* Header: Teams icon + Teams + now */}
            <div className="relative flex items-center gap-2">
              {/* Teams app icon */}
              <div className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#5B5FC7] shadow-sm">
                <TeamsLogo />
              </div>
              <span className="text-[13px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
                Teams
              </span>
              <span className="ml-auto text-[12px] text-[#8E8E93] dark:text-[#98989D]">
                {uiTexts?.now || "now"}
              </span>
            </div>

            {/* Sender name & message */}
            <div className="relative mt-1.5 pl-[44px]">
              <div className="flex items-center justify-between">
                <span className="text-[14px] font-semibold text-[#1C1C1E] dark:text-[#F2F2F7]">
                  {resolvedSenderName}
                </span>
                {/* Unread badge */}
                <span className="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-[#5B5FC7] px-1.5 text-[11px] font-bold text-white">
                  1
                </span>
              </div>
              {/* Message preview */}
              <div className="mt-0.5 pr-3 text-[14px] leading-snug text-[#3C3C43]/70 line-clamp-2 dark:text-[#EBEBF5]/60">
                {previewText}
              </div>
            </div>
          </button>

          {/* Home indicator */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2">
            <div className="h-1 w-28 rounded-full bg-black/15 dark:bg-white/20" />
          </div>
        </div>
      ) : (
        <div className={`${chatShellClassName} relative`}>
          {/* OTP Notification overlay */}
          {otpData && (
            <OtpNotification
              otpCode={otpData.code}
              otpSender={otpData.sender}
              codeLabel={otpTexts?.codeLabel}
              codeSuffix={otpTexts?.codeSuffix}
              backLabel={otpTexts?.backLabel}
              backHint={otpTexts?.backHint}
              nowLabel={uiTexts?.now}
            />
          )}
          {activeLandingPage && (
            <FakeLandingPage
              landingPage={activeLandingPage}
              onSubmit={handleLandingPageSubmit}
              onBack={handleLandingPageBack}
              backLabel={landingPageTexts?.backLabel}
            />
          )}
          {/* Teams header */}
          <div className="shrink-0 flex items-center gap-2 border-b border-[#E0E0E0] px-2 py-2" style={{ backgroundColor: "#F5F5F5" }}>
            <ChevronLeft size={18} className="shrink-0 text-[#616161]" />
            {/* Contact avatar */}
            <div className="h-8 w-8 shrink-0 rounded-full bg-[#5B5FC7] flex items-center justify-center text-sm font-bold text-white">
              {senderInitial}
            </div>
            {/* Name and status */}
            <div className="flex-1 min-w-0">
              <div className="text-[14px] font-semibold text-[#242424] leading-tight truncate">
                {resolvedSenderName}
              </div>
              <div className="text-[11px] text-[#616161] leading-tight">
                {status === "receiving" ? "Typing..." : "Available"}
              </div>
            </div>
            <MoreHorizontal size={18} className="shrink-0 text-[#616161]" />
          </div>

          {/* Chat area - Teams white bg */}
          <div
            ref={scrollRef}
            className="min-h-0 flex-1 overflow-y-auto px-3 py-3"
            style={{ backgroundColor: "#FFFFFF" }}
          >
            {messages.length === 0 && !isInitializing && (
              <div className="flex items-center justify-center py-8 text-xs text-[#616161]">
                {statusLabel}
              </div>
            )}

            <div className="flex flex-col gap-3">
              {messages.map((msg, idx) => (
                <div key={`${msg.role}-${idx}`} className="flex gap-2">
                  {/* Avatar */}
                  <div
                    className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-[11px] font-bold text-white ${
                      msg.role === "assistant" ? "bg-[#5B5FC7]" : "bg-[#0078D4]"
                    }`}
                  >
                    {msg.role === "assistant" ? senderInitial : userInitial}
                  </div>
                  {/* Message content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="text-[13px] font-semibold text-[#242424]">
                        {msg.role === "assistant" ? resolvedSenderName : userName}
                      </span>
                      <span className="text-[11px] text-[#616161]">
                        {msg.timestamp}
                      </span>
                    </div>
                    <div className={`mt-0.5 rounded-lg px-2.5 py-1.5 text-[13px] leading-snug break-words [overflow-wrap:anywhere] ${
                      msg.role === "user"
                        ? "bg-[#E8EBFA] text-[#242424]"
                        : "bg-[#F5F5F5] text-[#242424]"
                    }`}>
                      <ChatMessageContent
                        content={msg.content}
                        linkWarningTitle={linkWarningTitle}
                        linkWarningBody={linkWarningBody}
                        onLinkClick={handleLinkClick}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {status === "receiving" && (
                <TypingIndicator name={resolvedSenderName} />
              )}
            </div>
          </div>

          {/* Input area - Teams style */}
          <SmishingInputHelper
            category={expectedResponseCategory}
            language={language}
            disabled={isBusy || !hasStarted}
            onSelect={setInputValue}
          />
          <div className="shrink-0 border-t border-[#E0E0E0] px-2 pb-2 pt-1.5" style={{ backgroundColor: "#FFFFFF" }}>
            <div className="flex items-center gap-1 rounded-lg border border-[#E0E0E0] bg-[#F5F5F5] px-3 py-1.5">
              <input
                type="text"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                onKeyDown={(event) => event.key === "Enter" && sendMessage()}
                placeholder={hasStarted ? (uiTexts?.inputPlaceholder || "Type a new message") : statusLabel}
                disabled={isBusy || !hasStarted}
                className="flex-1 bg-transparent text-[13px] text-[#242424] placeholder-[#616161] focus:outline-none"
              />
              <button
                type="button"
                onClick={sendMessage}
                disabled={!inputValue.trim() || isBusy || !hasStarted}
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors ${
                  inputValue.trim() && !isBusy && hasStarted
                    ? "text-[#5B5FC7]"
                    : "text-[#C4C4C4]"
                } disabled:opacity-40`}
                aria-label="Send message"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}

      {errorMessage && (
        <div className="mt-2 text-xs text-rose-600 dark:text-rose-300">{errorMessage}</div>
      )}
    </div>
  );
}
